import { useEffect, useCallback, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { api } from '../utils/api';
import { useEditorStore } from '../store/editorStore';
import { useUpdateDraft, useCreateDraft, type Draft } from './useDataCenterApi';
import { buildStagedChangesJson, mapApiRack } from '../utils/draftUtils';
import { PPM } from '../store/editorStore';
import { shouldAutosaveCreateDraft } from '../utils/editorDrafts.logic';
import { executeDraftSaveFlow } from './draftSaveRevision.logic';
import { toast } from 'sonner';
import { clearPendingLocalOperation, writePendingLocalOperation } from '../utils/localOperationRecovery.logic';

export function useDraftAutoSave(
  code: string,
  currentDraftId: number | null,
  setCurrentDraftId: (id: number | null) => void,
  dc: any,
  hadDraftId: MutableRefObject<boolean>,
  currentDraftRevision: number | null,
) {
  const { setSaveStatus, saveStatus } = useEditorStore();
  const updateDraftMut = useUpdateDraft(code);
  const createDraftMut = useCreateDraft(code);
  const [reviewRequest, setReviewRequest] = useState<{ id: number; revision: number } | null>(null);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextSaveRevisionRef = useRef(0);
  const latestStartedRevisionRef = useRef(0);
  const draftRevisionRef = useRef<number | null>(currentDraftRevision);

  useEffect(() => {
    if (currentDraftId != null && currentDraftRevision != null) draftRevisionRef.current = currentDraftRevision;
  }, [currentDraftId, currentDraftRevision]);

  const executeSave = useCallback(async (draftId: number) => {
    try {
      const store = useEditorStore.getState();
      const sentRevision = ++nextSaveRevisionRef.current;
      latestStartedRevisionRef.current = sentRevision;
      const sentRacks = JSON.parse(JSON.stringify(store.racks));
      const racksJson = buildStagedChangesJson(store.racks, store.baselineRacks);
      const pendingOperationId = `${Date.now()}-${sentRevision}`;
      const defaultTrayZ = (store.dcSettings?.defaultRackHeightM ?? 2.0) + (store.dcSettings?.defaultTrayHeightM ?? 0.3);
      const cableTraysJson = {
        nodes: Object.values(store.trayNodes).map((n: any) => {
          const zVal = n.z_m ?? n.z ?? defaultTrayZ;
          return {
            ...n,
            x: Number((n.x / PPM).toFixed(3)),
            y: Number((n.y / PPM).toFixed(3)),
            z: zVal,
            z_m: zVal,
          };
        }),
        edges: Object.values(store.trayEdges),
      };
      setSaveStatus('saving');
      // Serialize aisle_zones with metre coordinates for persistence
      const aisleZonesPayload = Object.fromEntries(
        Object.entries(store.aisleZones).map(([id, z]: [string, any]) => [
          id,
          { ...z, x_m: +(z.x / PPM).toFixed(3), y_m: +(z.y / PPM).toFixed(3), w_m: +(z.w / PPM).toFixed(3), h_m: +(z.h / PPM).toFixed(3) },
        ])
      );
      // Serialize racks_meta (status and role) for draft persistence
      const racksMetaPayload = Object.fromEntries(
        Object.values(store.racks).map((r: any) => [
          r.id,
          { status: r.status || 'active', role: r.role || 'Server' }
        ])
      );
      const pendingOperation = {
        id: pendingOperationId,
        code,
        draftId,
        expectedRevision: draftRevisionRef.current,
        racksJson,
        cableTraysJson: {
          ...cableTraysJson,
          rows_meta: store.rows,
          dc_settings: store.dcSettings,
          aisle_zones: aisleZonesPayload,
          racks_meta: racksMetaPayload,
        },
        operation: racksJson.operation,
        createdAt: Date.now(),
      };
      // Keep only an unconfirmed request. A successful response clears this exact entry.
      writePendingLocalOperation(typeof window === 'undefined' ? undefined : window.localStorage, pendingOperation);
      return executeDraftSaveFlow({
        sentRevision,
        getLatestStartedRevision: () => latestStartedRevisionRef.current,
        sentRacks,
        request: async () => {
          const result = await updateDraftMut.mutateAsync({
            draftId,
            expected_revision: draftRevisionRef.current ?? undefined,
            racks_json: racksJson,
            cable_trays_json: {
            ...cableTraysJson,
            rows_meta: store.rows,
            dc_settings: store.dcSettings,
            aisle_zones: aisleZonesPayload,
            racks_meta: racksMetaPayload,
            },
          });
          clearPendingLocalOperation(typeof window === 'undefined' ? undefined : window.localStorage, code, draftId, pendingOperationId);
          if (sentRevision === latestStartedRevisionRef.current) draftRevisionRef.current = result.revision;
          return result;
        },
        getCurrentState: () => {
          const { racks, saveStatus } = useEditorStore.getState();
          return { racks, saveStatus };
        },
        applyState: acknowledgement => useEditorStore.setState(acknowledgement),
        reportFailure: () => setSaveStatus('error'),
        onRejected: err => console.error('[AutoSave] executeSave error:', err),
      });
    } catch (err) {
      console.error('[AutoSave] executeSave preparation error:', err);
      setSaveStatus('error');
      return null;
    }
  }, [updateDraftMut, setSaveStatus]);

  const handleSave = useCallback(async (source?: 'autosave') => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    let draftId = currentDraftId;
    let createdThisSave = false;
    if (!draftId) {
      if (source === 'autosave' && !shouldAutosaveCreateDraft(currentDraftId, hadDraftId.current)) {
        return;
      }
      try {
        const draft = await createDraftMut.mutateAsync({ name: `Черновик ${new Date().toLocaleString('ru-RU')}`, rows_count: dc?.rows_count ?? 1, max_racks_per_row: dc?.max_racks_per_row ?? 10 });
        draftId = draft.id;
        draftRevisionRef.current = draft.revision;
        createdThisSave = true;
        // Defer setCurrentDraftId until after executeSave — otherwise useDraftLoader
        // fetches the empty new draft and wipes Wizard racks (audit P0).
      } catch (err: any) {
        console.error('[AutoSave] handleSave createDraft error:', err);
        setSaveStatus('error');
        return;
      }
    }
    const acceptedRevision = await executeSave(draftId!);
    if (createdThisSave) setCurrentDraftId(draftId!);
    return acceptedRevision;
  }, [currentDraftId, createDraftMut, dc, executeSave, setSaveStatus, setCurrentDraftId, hadDraftId]);

  useEffect(() => {
    if (saveStatus !== 'dirty') return;
    if (currentDraftId == null && !shouldAutosaveCreateDraft(currentDraftId, hadDraftId.current)) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => handleSave('autosave'), 2000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [saveStatus, handleSave, currentDraftId, hadDraftId]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); handleSave(); }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'F') { e.preventDefault(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleSave]);

  const handlePublish = useCallback(async () => {
    if (!currentDraftId) return;
    try {
      const status = useEditorStore.getState().saveStatus;
      if (status === 'saving') return;
      // A clean draft must not be saved again: it would invalidate its approval.
      const savedDraft = status === 'dirty' || status === 'error'
        ? await handleSave()
        : await api.get(`/api/datacenters/${code}/drafts/${currentDraftId}`).then(r => r.data as Draft);
      if (savedDraft) setReviewRequest({ id: currentDraftId, revision: savedDraft.revision });
    } catch (err) {
      toast.error('Не удалось подготовить сохранённый черновик для review.');
      console.error('Ошибка review', err);
    }
  }, [currentDraftId, handleSave, code]);

  const onReviewPublished = useCallback(async () => {
    const response = await api.get(`/api/datacenters/${code}/racks`);
    const racksRecord = Object.fromEntries((response.data as any[]).map(rack => {
      const mapped = mapApiRack(rack);
      return [mapped.id, mapped];
    }));
    useEditorStore.getState().loadRacks(racksRecord);
    useEditorStore.getState().setBaselineRacks(JSON.parse(JSON.stringify(racksRecord)));
    setSaveStatus('idle');
    setCurrentDraftId(null);
  }, [code, setSaveStatus, setCurrentDraftId]);

  return { handleSave, handlePublish, reviewRequest, closeReview: () => setReviewRequest(null), onReviewPublished };
}
